import api from "./api";
import { getBackendUrl } from "../config";

/**
 * Cliente HTTP exclusivamente para rechazar llamadas.
 *
 * Por defecto NO permite enviar solicitudes.
 * No usa una URL alternativa ni cambia de servidor.
 * Nunca envia el userId, credenciales o SDP en el body.
 *
 * La autorizacion definitiva permanece en el backend.
 */

const getSetting = name => {
  if (
    typeof window !== "undefined" &&
    window.ENV !== undefined
  ) {
    return window.ENV[name] || "";
  }

  return import.meta.env[name] || "";
};

const safeOrigin = raw => {
  if (
    typeof raw !== "string" ||
    !raw ||
    raw.trim() !== raw
  ) {
    return null;
  }

  try {
    const url = new URL(raw);

    if (
      !url.hostname ||
      url.username ||
      url.password ||
      url.search ||
      url.hash ||
      url.pathname !== "/"
    ) {
      return null;
    }

    // Nunca utilizar la API principal de MandaExpress
    // para probar el rechazo de llamadas.
    if (url.hostname.toLowerCase() ===
        "api.mandaexpress.com.mx") {
      return null;
    }

    const local = [
      "localhost",
      "127.0.0.1",
      "::1",
      "[::1]"
    ].includes(url.hostname.toLowerCase());

    if (
      url.protocol !== "https:" &&
      !(local && url.protocol === "http:")
    ) {
      return null;
    }

    return url.origin;
  } catch (_error) {
    return null;
  }
};

/**
 * Requiere dos valores EXPLICITOS en el frontend
 * de staging:
 *
 * VITE_META_CALL_REJECT_UI_ENABLED=true
 * VITE_META_CALL_STAGING_API_ORIGIN=<origen verificado>
 *
 * El origen fijado tiene que coincidir con Axios
 * y con getBackendUrl().
 *
 * No configurar estos valores hasta verificar que
 * existe un backend de pruebas separado.
 */
export const isMetaCallRejectConfigured = () => {
  if (
    getSetting("VITE_META_CALL_REJECT_UI_ENABLED") !== "true"
  ) {
    return false;
  }

  const pinned = safeOrigin(
    getSetting("VITE_META_CALL_STAGING_API_ORIGIN")
  );

  const backend = safeOrigin(getBackendUrl());
  const axiosOrigin = safeOrigin(api.defaults.baseURL);

  return Boolean(
    pinned &&
    backend &&
    axiosOrigin &&
    pinned === backend &&
    pinned === axiosOrigin
  );
};

/**
 * Reclama y despues rechaza una llamada.
 * Dos solicitudes distintas, sin reintentos.
 *
 * El backend debe comprobar la asignacion vigente
 * en ambas operaciones.
 */
export const rejectMetaCall = async callId => {
  if (!isMetaCallRejectConfigured()) {
    throw new Error("META_CALL_REJECT_DISABLED");
  }

  if (
    typeof callId !== "string" ||
    !callId ||
    callId.length > 256 ||
    callId.trim() !== callId ||
    /[\x00-\x1f\x7f]/.test(callId)
  ) {
    throw new Error("META_CALL_INVALID_ID");
  }

  const path = "/meta/calls/" +
    encodeURIComponent(callId);

  // El interceptor existente de Axios aporta el JWT.
  // No se acepta identidad declarada por el navegador.
  const claim = await api.post(
    path + "/claim",
    {}, { _retry: true });

  if (
    claim.status !== 200 ||
    claim.data?.status !== "reserved"
  ) {
    throw new Error("META_CALL_CLAIM_UNAVAILABLE");
  }

  const reject = await api.post(
    path + "/reject",
    {}, { _retry: true });

  if (
    reject.status === 200 &&
    reject.data?.status === "provider_confirmed"
  ) {
    return "provider_confirmed";
  }

  if (
    reject.status === 202 &&
    reject.data?.status === "uncertain"
  ) {
    return "uncertain";
  }

  throw new Error("META_CALL_REJECT_UNAVAILABLE");
};
